import React, { useEffect, useState } from 'react';
import {OutflowRowProps, OutflowCard } from '../../component/Outflow';
import CardGallery from '../../component/CardGallery'
import { useSearchParams } from 'react-router-dom';
import { FilterOption } from '../../component/FilterBar';
import FilterBar from '../../component/FilterBar';
import Loading from '../../component/Loading';

interface FilterOptions {
  models: string[];
  part_names: string[];
  part_numbers: string[];
  systems: string[];
}

// Define a cache duration (e.g., 10 minutes)
const CACHE_DURATION_MS = 10 * 60 * 1000;

interface FilterCache {
  timestamp: number;
  data: FilterOptions;
}

const TeachmodePage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(parseInt(searchParams.get('page') || '1', 10));
  const [pageSize, setPageSize] = useState(parseInt(searchParams.get('limit') || '20', 10));
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [filterLoading, setFilterLoading] = useState(true);
  const [sortBy, setSortBy]       = useState<string>(searchParams.get('sort_by') || 'timestamp');
  const [sortOrder, setSortOrder] = useState<'asc'|'desc'>( (searchParams.get('sort_order') as any) || 'desc' );

  // State for filter options from the backend
  const [filterOptions, setFilterOptions] = useState<FilterOptions>({
    models: [],
    part_names: [],
    part_numbers: [],
    systems: []
  });

  // Initialize filters from URL params
  const [filters, setFilters] = useState({
    part_name: getArrayParamFromUrl('part_name'),
    part_number: getArrayParamFromUrl('part_number'),
    model: getArrayParamFromUrl('model'),
    system_name: getArrayParamFromUrl('system_name'),
    time_range: getArrayParamFromUrl('time_range'),
  });
  
  const [outflow, setOutflow] = useState<OutflowRowProps[]>([])

  // Helper function to get array parameters from URL
  function getArrayParamFromUrl(paramName: string): string[] {
    const params = searchParams.getAll(paramName);
    return params.length > 0 ? params : [];
  }

  // Update URL with current filters
  const updateUrlWithFilters = (page: number, limit: number, filterValues: Record<string, any>) => {
    const newParams = new URLSearchParams();
    
    // Add pagination params
    newParams.set('page', page.toString());
    newParams.set('limit', limit.toString());
    
    // Add filter params
    Object.entries(filterValues).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        // Clear previous values before adding new ones
        if (value.length > 0) {
          value.forEach(val => newParams.append(key, val));
        }
      } else if (value) {
        newParams.set(key, value);
      }
    });
    
    // Update the URL without reloading the page
    setSearchParams(newParams);
  };

  useEffect(() => {
    fetchOutflow(currentPage, pageSize, filters);
    // Try to load filters from cache first
    const cachedFilters = getFilterOptionsFromCache();
    if (cachedFilters) {
      setFilterOptions(cachedFilters);
      setFilterLoading(false);
    } else {
      fetchFilterOptions();
    }
  }, []);
  
  useEffect(() => {
    if (currentPage !== 1) {
      fetchOutflow(currentPage, pageSize, filters);
    }
  }, [currentPage]);

  const getFilterOptionsFromCache = (): FilterOptions | null => {
    try {
      const cachedData = localStorage.getItem('filterOptionsCache');
      if (cachedData) {
        const cache: FilterCache = JSON.parse(cachedData);
        const now = new Date().getTime();
        if (now - cache.timestamp < CACHE_DURATION_MS) {
          console.log('Using cached filter options');
          return cache.data;
        } else {
          console.log('Cache expired, fetching new data');
        }
      }
    } catch (error) {
      console.error('Error reading from cache:', error);
    }
    return null;
  };

  const saveFilterOptionsToCache = (data: FilterOptions) => {
    try {
      const cacheData: FilterCache = {
        timestamp: new Date().getTime(),
        data: data
      };
      localStorage.setItem('filterOptionsCache', JSON.stringify(cacheData));
      console.log('Filter options saved to cache');
    } catch (error) {
      console.error('Error saving to cache:', error);
    }
  };

  const fetchFilterOptions = async () => {
    setFilterLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/filter-options`, {
        method: 'GET',
        credentials: 'include',
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch filter options');
      }
      
      const data = await response.json();
      setFilterOptions(data);
      saveFilterOptionsToCache(data);
    } catch (error) {
      console.error('Error fetching filter options:', error);
    } finally {
      setFilterLoading(false);
    }
  };

  const handleFilterChange = (key: string, value: string | string[]) => {
    setFilters(prevFilters => ({ ...prevFilters, [key]: value }));
  };

  const handleApplyFilters = () => {
    setCurrentPage(1); // Reset to first page when applying new filters
    fetchOutflow(1, pageSize, filters);
  };

  async function fetchOutflow(page: number, size: number, filterValues: Record<string, any>) {
    setLoading(true);
    try {
      const queryParams = new URLSearchParams({
        page: page.toString(),
        limit: size.toString(),
      });
      
      // Append each filter to query parameters
      Object.entries(filterValues).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          if (value.length > 0) {
            // Append each value separately (e.g., ?model=ModelA&model=ModelB)
            value.forEach(val => queryParams.append(key, val));
          }
        } else if (value) {
          queryParams.append(key, value);
        }
      });

      // Update URL with the current filters
      updateUrlWithFilters(page, size, filterValues);

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/teachmode?${queryParams.toString()}`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        }
      });
      if (!response.ok) {
        throw new Error('Failed to fetch outflow data.');
      }
      const data = await response.json();
      console.log(data)
      setOutflow(data.items);
      setTotalPages(data.total_pages);
      setTotalItems(data.total_count);
    } catch (error) {
      console.error('Error fetching outflow data:', error);
    } finally {
      setLoading(false);
    }
  }

  console.log(outflow)
  const filterBarOptions: FilterOption[] = [
    {
      name: "part_name",
      label: "Part Name",
      type: "select",
      options: filterOptions.part_names,
    },
    {
      name: "part_number",
      label: "Part Number",
      type: "select",
      options: filterOptions.part_numbers,
    },
    {
      name: "model",
      label: "Model",
      type: "select",
      options: filterOptions.models,
    },
    {
      name: "system_name",
      label: "System Name",
      type: "select",
      options: filterOptions.systems,
    },
    {
      name: "time_range",
      label: "Time Range",
      type: "timerange", // New timerange filter option.
    },
  ];
  return (
    <div className="flex h-screen">
      <main className="flex-1 bg-gray-500 p-6">
      {loading && <Loading message="Loading Gallery..." />}
        <FilterBar
          filters={filters}
          filterOptions={filterBarOptions}
          onFilterChange={handleFilterChange}
          onApplyFilters={handleApplyFilters}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSortChange={(newSortBy, newSortOrder) => {
          setSortBy(newSortBy);
          setSortOrder(newSortOrder);
          fetchOutflow(currentPage, pageSize, filters);
        }}
          isLoading={filterLoading}
          />
          <CardGallery
            CardComponent={OutflowCard}
            cardClickPath='/teachmode'
            data={outflow}
            loading={loading}
            onPageChange={(page: number, size?: number) => {
              setCurrentPage(page);
              if (size && size !== pageSize) {
                setPageSize(size);
                fetchOutflow(1, size, filters);
              } else {
                fetchOutflow(page, pageSize, filters);
              }
            }}
            itemsPerPage={pageSize}
            totalItems={totalItems}
            totalPages={totalPages}
            currentPage={currentPage}
          />
      </main>
    </div>
  );
};

export default TeachmodePage;